// Dojo Theme Switcher — five seasonal palettes that retint the entire
// Bet Cracker UI by swapping CSS custom properties on the <html> element.
//
// Each theme keeps the dark "futuristic dojo" core but rotates the accent
// colours, glow halos and ambient particles. The default ("shadow") matches
// the original neon-green look so existing screens stay pixel-identical
// until the user explicitly picks a new mood.

export const DOJO_THEMES = [
  {
    id: "shadow",
    label: "Shadow Dojo",
    season: "Eterno",
    blurb: "Il dojo originale. Verde neon su nero infinito.",
    swatch: ["#22C55E", "#F6C643", "#0A0A0A"],
    ambience: "lasers",       // subtle horizontal laser lines
    accent: "#22C55E",
    accent2: "#F6C643",
    glow: "rgba(34,197,94,0.55)",
    bg: "#050505",
    panel: "#0A0A0A",
    ink: "#FFFFFF",
    sub: "rgba(255,255,255,0.55)",
    danger: "#DC2626",
    kanji: "道",                  // Dō — The Way (of Bushido)
    kanjiMeaning: "La Via",
    glyph: "torii",
  },
  {
    id: "sakura",
    label: "Sakura",
    season: "Primavera",
    blurb: "Petali di ciliegio che danzano nel vento di Kyoto.",
    swatch: ["#F8B7CE", "#FFD7E5", "#180A12"],
    ambience: "petals",
    accent: "#F8B7CE",
    accent2: "#FFD7E5",
    glow: "rgba(248,183,206,0.55)",
    bg: "#0B0407",
    panel: "#150810",
    ink: "#FFF5F8",
    sub: "rgba(255,245,248,0.55)",
    danger: "#FF5C7A",
    kanji: "桜",                  // Sakura — Cherry blossom
    kanjiMeaning: "Ciliegio",
    glyph: "blossom",
  },
  {
    id: "momiji",
    label: "Momiji",
    season: "Autunno",
    blurb: "L'acero rosso brucia mentre il sole si china sul tempio.",
    swatch: ["#E07A2C", "#C4391C", "#180D04"],
    ambience: "leaves",
    accent: "#E07A2C",
    accent2: "#F2B23E",
    glow: "rgba(224,122,44,0.55)",
    bg: "#0A0604",
    panel: "#160C06",
    ink: "#FFF1E0",
    sub: "rgba(255,241,224,0.58)",
    danger: "#C4391C",
    kanji: "楓",                  // Kaede — Maple
    kanjiMeaning: "Acero",
    glyph: "maple",
  },
  {
    id: "yuki",
    label: "Yuki",
    season: "Inverno",
    blurb: "Neve silenziosa scende sul Fuji al crepuscolo.",
    swatch: ["#82C8FF", "#C8E0FF", "#080F1A"],
    ambience: "snow",
    accent: "#82C8FF",
    accent2: "#C8E0FF",
    glow: "rgba(130,200,255,0.6)",
    bg: "#060A12",
    panel: "#0C1422",
    ink: "#F0F8FF",
    sub: "rgba(240,248,255,0.55)",
    danger: "#FF6B8A",
    kanji: "雪",                  // Yuki — Snow
    kanjiMeaning: "Neve",
    glyph: "fuji",
  },
  {
    id: "kintsugi",
    label: "Kintsugi",
    season: "Maestria",
    blurb: "Oro liquido nelle crepe del vaso. La bellezza dell'imperfezione.",
    swatch: ["#D4A744", "#B8862E", "#0A0703"],
    ambience: "sparks",
    accent: "#D4A744",
    accent2: "#F2D27A",
    glow: "rgba(212,167,68,0.6)",
    bg: "#070502",
    panel: "#120D04",
    ink: "#FFF6E0",
    sub: "rgba(255,246,224,0.6)",
    danger: "#E04E2C",
    kanji: "金",                  // Kin — Gold
    kanjiMeaning: "Oro",
    glyph: "circle-broken",
  },
];

// ─── Hinode (Light) overrides per ogni tema ─────────────────────────
// Hinode ("alba" — 日の出) è il MODE chiaro: applicabile sopra qualsiasi
// tema esistente, modifica solo i 5 colori "neutri" (bg, panel, ink,
// sub, border + glow scaling). L'accent del tema selezionato resta
// invariato perché è la firma visiva del tema (es. Sakura rosa, Yuki
// blu). Le ombre neon vengono attenuate in light per evitare l'effetto
// "alone bavoso" su sfondi chiari.
export const HINODE_OVERRIDES = {
  bg: "#FAF8F4",
  panel: "#FFFFFF",
  panelElev: "#F1ECE3",
  ink: "#171717",
  sub: "rgba(23,23,23,0.62)",
  border: "rgba(23,23,23,0.10)",
  borderStrong: "rgba(23,23,23,0.20)",
  shadow: "0 1px 3px rgba(23,23,23,0.08), 0 2px 8px rgba(23,23,23,0.04)",
  glowScale: 0.30,
};

export const YAMI_BASE = {
  panelElev: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.18)",
  shadow: "0 0 0 transparent",
  glowScale: 1.0,
};

export const MODES = {
  dark: { id: "dark", label: "Yami · Notte", icon: "moon" },
  light: { id: "light", label: "Hinode · Alba", icon: "sun" },
};

const STORAGE_KEY = "betcracker_dojo_theme_v1";
const MODE_STORAGE_KEY = "betcracker_dojo_mode_v1";
const DEFAULT_ID = "shadow";
const DEFAULT_MODE = "dark";

export function getThemeById(id) {
  return DOJO_THEMES.find((t) => t.id === id) || DOJO_THEMES[0];
}

export function getStoredThemeId() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && DOJO_THEMES.some((t) => t.id === stored)) return stored;
  } catch { /* ignore */ }
  return DEFAULT_ID;
}

export function setStoredThemeId(id) {
  try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
}

export function getStoredMode() {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch { /* ignore */ }
  return DEFAULT_MODE;
}

export function setStoredMode(mode) {
  try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch { /* ignore */ }
}

/**
 * Apply theme + mode to <html>. CSS vars get written so the entire UI
 * re-tints instantly. Components read via ``var(--bc-*)``.
 * @param {string} id  theme id (shadow/sakura/momiji/yuki/kintsugi)
 * @param {string} mode  "dark" | "light"
 */
export function applyDojoTheme(id, mode) {
  const theme = getThemeById(id);
  const resolvedMode = mode || getStoredMode();
  const isLight = resolvedMode === "light";
  const root = document.documentElement;
  root.dataset.dojoTheme = theme.id;
  root.dataset.dojoMode = resolvedMode;
  root.dataset.dojoAmbience = theme.ambience;

  // Accent del tema — non cambia mai con il mode
  root.style.setProperty("--bc-accent", theme.accent);
  root.style.setProperty("--bc-accent-2", theme.accent2);
  root.style.setProperty("--bc-glow", theme.glow);
  root.style.setProperty("--bc-danger", theme.danger);

  // Neutri — switchano col mode
  if (isLight) {
    root.style.setProperty("--bc-bg", HINODE_OVERRIDES.bg);
    root.style.setProperty("--bc-panel", HINODE_OVERRIDES.panel);
    root.style.setProperty("--bc-panel-elev", HINODE_OVERRIDES.panelElev);
    root.style.setProperty("--bc-ink", HINODE_OVERRIDES.ink);
    root.style.setProperty("--bc-sub", HINODE_OVERRIDES.sub);
    root.style.setProperty("--bc-border", HINODE_OVERRIDES.border);
    root.style.setProperty("--bc-border-strong", HINODE_OVERRIDES.borderStrong);
    root.style.setProperty("--bc-shadow", HINODE_OVERRIDES.shadow);
    root.style.setProperty("--bc-glow-scale", String(HINODE_OVERRIDES.glowScale));
    // Neon glow attenuato per non sbavare su bianco
    root.style.setProperty("--bc-glow-soft", `color-mix(in srgb, ${theme.accent} 18%, transparent)`);
  } else {
    root.style.setProperty("--bc-bg", theme.bg);
    root.style.setProperty("--bc-panel", theme.panel);
    root.style.setProperty("--bc-panel-elev", YAMI_BASE.panelElev);
    root.style.setProperty("--bc-ink", theme.ink);
    root.style.setProperty("--bc-sub", theme.sub);
    root.style.setProperty("--bc-border", YAMI_BASE.border);
    root.style.setProperty("--bc-border-strong", YAMI_BASE.borderStrong);
    root.style.setProperty("--bc-shadow", YAMI_BASE.shadow);
    root.style.setProperty("--bc-glow-scale", String(YAMI_BASE.glowScale));
    root.style.setProperty("--bc-glow-soft", `color-mix(in srgb, ${theme.accent} 35%, transparent)`);
  }

  // Translucent variants accent — funzionano in entrambi i mode
  root.style.setProperty("--bc-accent-soft",   `color-mix(in srgb, ${theme.accent} 10%, transparent)`);
  root.style.setProperty("--bc-accent-soft-2", `color-mix(in srgb, ${theme.accent} 18%, transparent)`);
  root.style.setProperty("--bc-accent-medium", `color-mix(in srgb, ${theme.accent} 32%, transparent)`);
  root.style.setProperty("--bc-accent-strong", `color-mix(in srgb, ${theme.accent} 55%, transparent)`);

  window.dispatchEvent(new CustomEvent("dojo-theme-changed", { detail: { ...theme, mode: resolvedMode } }));
  return theme;
}

export const DEFAULT_THEME_ID = DEFAULT_ID;
export const DEFAULT_MODE_ID = DEFAULT_MODE;
